import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { fmtBRL, fmtCompetencia, fmtDate } from '../../lib/format';
import { AlertCircle, Search, ChevronRight, ChevronDown } from 'lucide-react';

interface Resumo {
  totalAssociados: number;
  totalParcelas: number;
  totalSaldo: number;
  porFaixa: Array<{ faixa: string; qtdAssociados: number; qtdParcelas: number; saldo: number }>;
}

interface ParcelaInadimplente {
  parcelaId: string;
  numeroParcela: number;
  competencia: string;
  vencimento: string;
  diasAtraso: number;
  valorPrevisto: number;
  valorPago: number;
  saldoDevedor: number;
  status: string;
  ultimaTentativaEtipi: { competencia: string; statusEtipi: string; descricao: string } | null;
}

interface AssociadoInadimplente {
  associadoId: string;
  nome: string;
  documentoMask: string;
  matricula: string;
  orgaoPublico: string | null;
  agente: { id: string; nome: string } | null;
  totalParcelasAtrasadas: number;
  totalSaldoDevedor: number;
  diasAtrasoMaximo: number;
  contratos: Array<{
    contratoId: string;
    numeroContrato: string;
    etapaOperacional: string;
    statusFinanceiro: string;
    parcelas: ParcelaInadimplente[];
  }>;
}

function corFaixa(dias: number): string {
  if (dias > 90) return 'bg-red-100 text-red-900';
  if (dias > 60) return 'bg-orange-100 text-orange-900';
  if (dias > 30) return 'bg-amber-100 text-amber-900';
  return 'bg-yellow-100 text-yellow-900';
}

export function InadimplenciaPage() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [expandido, setExpandido] = useState<Record<string, boolean>>({});

  const { data: resumo } = useQuery({
    queryKey: ['inadimplencia-resumo'],
    queryFn: () => api<Resumo>('/inadimplencia/resumo'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['inadimplencia', { q: search }],
    queryFn: () => {
      const params = new URLSearchParams({ size: '200' });
      if (search) params.set('q', search);
      return api<{ items: AssociadoInadimplente[]; total: number; totalSaldoGeral: number }>(
        `/inadimplencia/?${params.toString()}`,
      );
    },
  });

  function toggle(id: string) {
    setExpandido(e => ({ ...e, [id]: !e[id] }));
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
          Inadimplência
          <AlertCircle className="h-6 w-6 text-red-500" />
        </h1>
        <p className="text-muted-foreground">Parcelas vencidas sem pagamento integral</p>
      </div>

      {resumo && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Associados inadimplentes</CardDescription>
              <CardTitle className="text-3xl">{resumo.totalAssociados.toLocaleString('pt-BR')}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Parcelas em atraso</CardDescription>
              <CardTitle className="text-3xl">{resumo.totalParcelas.toLocaleString('pt-BR')}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Saldo total devedor</CardDescription>
              <CardTitle className="text-3xl text-red-600">{fmtBRL(resumo.totalSaldo)}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {resumo && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Por faixa de atraso</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {resumo.porFaixa.map((f) => (
                <div key={f.faixa} className="border rounded-md p-3">
                  <p className="text-xs text-muted-foreground">{f.faixa}</p>
                  <p className="text-lg font-semibold mt-1">{f.qtdAssociados}</p>
                  <p className="text-xs text-muted-foreground">
                    {f.qtdParcelas} parcela{f.qtdParcelas === 1 ? '' : 's'} · {fmtBRL(f.saldo)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex gap-2 items-center">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, CPF ou matrícula…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setSearch(q); }}
              className="flex-1"
            />
            <Button variant="secondary" onClick={() => setSearch(q)}>Buscar</Button>
            {search && <Button variant="ghost" size="sm" onClick={() => { setQ(''); setSearch(''); }}>limpar</Button>}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <Spinner />}
          {!isLoading && data && data.items.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">
              Nenhum associado inadimplente {search ? 'pra esse filtro' : 'no momento'}. 🎉
            </p>
          )}
          {!isLoading && data && data.items.length > 0 && (
            <div className="space-y-2">
              {data.items.map((a) => {
                const isOpen = expandido[a.associadoId] ?? false;
                return (
                  <div key={a.associadoId} className="border rounded-md">
                    <button
                      onClick={() => toggle(a.associadoId)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/30 text-left"
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      <div className="flex-1 min-w-0">
                        <Link to={`/associados/${a.associadoId}`} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
                          {a.nome}
                        </Link>
                        <p className="text-xs text-muted-foreground font-mono">
                          {a.documentoMask} · matr. {a.matricula} {a.orgaoPublico && ` · ${a.orgaoPublico}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-red-600">{fmtBRL(a.totalSaldoDevedor)}</p>
                        <p className="text-xs text-muted-foreground">{a.totalParcelasAtrasadas} parcela{a.totalParcelasAtrasadas === 1 ? '' : 's'}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded font-mono whitespace-nowrap ${corFaixa(a.diasAtrasoMaximo)}`}>
                        {a.diasAtrasoMaximo}d
                      </span>
                    </button>

                    {isOpen && (
                      <div className="border-t bg-muted/20 px-4 py-3 space-y-3">
                        {a.contratos.map((c) => (
                          <div key={c.contratoId}>
                            <div className="flex items-center gap-2 mb-2">
                              <Link to={`/contratos/${c.contratoId}`} className="text-sm text-primary hover:underline font-mono">
                                {c.numeroContrato}
                              </Link>
                              <span className="text-xs px-2 py-0.5 rounded bg-muted">{c.etapaOperacional}</span>
                              <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-900">{c.statusFinanceiro}</span>
                            </div>
                            <table className="w-full text-xs">
                              <thead className="text-muted-foreground">
                                <tr>
                                  <th className="text-left py-1 pr-3">Parcela</th>
                                  <th className="text-left py-1 pr-3">Competência</th>
                                  <th className="text-left py-1 pr-3">Vencimento</th>
                                  <th className="text-center py-1 pr-3">Atraso</th>
                                  <th className="text-right py-1 pr-3">Previsto</th>
                                  <th className="text-right py-1 pr-3">Pago</th>
                                  <th className="text-right py-1 pr-3">Saldo</th>
                                  <th className="text-left py-1">Última tentativa ETIPI</th>
                                </tr>
                              </thead>
                              <tbody>
                                {c.parcelas.map((p) => (
                                  <tr key={p.parcelaId} className="border-t border-muted/40">
                                    <td className="py-1 pr-3">#{p.numeroParcela}</td>
                                    <td className="py-1 pr-3">{fmtCompetencia(p.competencia)}</td>
                                    <td className="py-1 pr-3">{fmtDate(p.vencimento)}</td>
                                    <td className="py-1 pr-3 text-center">
                                      <span className={`px-1.5 py-0.5 rounded ${corFaixa(p.diasAtraso)}`}>{p.diasAtraso}d</span>
                                    </td>
                                    <td className="py-1 pr-3 text-right">{fmtBRL(p.valorPrevisto)}</td>
                                    <td className="py-1 pr-3 text-right">{p.valorPago > 0 ? fmtBRL(p.valorPago) : '—'}</td>
                                    <td className="py-1 pr-3 text-right font-medium text-red-600">{fmtBRL(p.saldoDevedor)}</td>
                                    <td className="py-1 text-muted-foreground">
                                      {p.ultimaTentativaEtipi ? (
                                        <span>
                                          <span className="font-mono mr-1">{p.ultimaTentativaEtipi.statusEtipi}</span>
                                          {p.ultimaTentativaEtipi.descricao}
                                        </span>
                                      ) : (
                                        <span className="italic">nenhuma tentativa registrada</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
